import { useState } from "react";

const INDUSTRIES = [
  "E-commerce / DTC",
  "SaaS",
  "Local business",
  "Professional services",
  "Media / Publishing",
  "Healthcare",
  "Finance",
  "Education",
  "Marketplace",
  "Other",
];

export interface IntakeDetails {
  website_url: string;
  full_name: string;
  email: string;
  company: string;
  industry: string;
  competitors: string;
  target_audience: string;
  notes: string;
}

interface Props {
  value: IntakeDetails;
  onChange: (next: IntakeDetails) => void;
  onSubmit: () => void;
  onBack: () => void;
}

const labelCls = "text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground";
const inputCls =
  "mt-1.5 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-foreground focus:outline-none";

export function DetailsForm({ value, onChange, onSubmit, onBack }: Props) {
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof IntakeDetails>(k: K, v: IntakeDetails[K]) =>
    onChange({ ...value, [k]: v });

  const validate = (): string | null => {
    if (!/^https?:\/\/\S+\.\S+/.test(value.website_url.trim())) {
      return "Enter a valid URL starting with http(s)://";
    }
    if (value.full_name.trim().length < 1) return "Your name is required";
    if (!/^\S+@\S+\.\S+$/.test(value.email.trim())) return "Enter a valid email";
    return null;
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const err = validate();
        setError(err);
        if (!err) onSubmit();
      }}
      className="grid gap-5"
    >
      <div>
        <label htmlFor="website_url" className={labelCls}>
          Website URL <span style={{ color: "#C8A84B" }}>*</span>
        </label>
        <input
          id="website_url"
          type="url"
          required
          inputMode="url"
          placeholder="https://yourstore.com"
          value={value.website_url}
          onChange={(e) => set("website_url", e.target.value)}
          className={inputCls}
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="full_name" className={labelCls}>
            Full name <span style={{ color: "#C8A84B" }}>*</span>
          </label>
          <input
            id="full_name"
            type="text"
            required
            value={value.full_name}
            onChange={(e) => set("full_name", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="email" className={labelCls}>
            Email <span style={{ color: "#C8A84B" }}>*</span>
          </label>
          <input
            id="email"
            type="email"
            required
            value={value.email}
            onChange={(e) => set("email", e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="company" className={labelCls}>
            Company
          </label>
          <input
            id="company"
            type="text"
            value={value.company}
            onChange={(e) => set("company", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="industry" className={labelCls}>
            Industry
          </label>
          <select
            id="industry"
            value={value.industry}
            onChange={(e) => set("industry", e.target.value)}
            className={inputCls}
          >
            <option value="">Select industry…</option>
            {INDUSTRIES.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="competitors" className={labelCls}>
          Top 3 competitors <span className="text-muted-foreground">(optional)</span>
        </label>
        <input
          id="competitors"
          type="text"
          placeholder="competitor1.com, competitor2.com, competitor3.com"
          value={value.competitors}
          onChange={(e) => set("competitors", e.target.value)}
          className={inputCls}
        />
      </div>

      <div>
        <label htmlFor="target_audience" className={labelCls}>
          Target audience <span className="text-muted-foreground">(optional)</span>
        </label>
        <input
          id="target_audience"
          type="text"
          placeholder="e.g. millennial women shopping for sustainable activewear"
          value={value.target_audience}
          onChange={(e) => set("target_audience", e.target.value)}
          className={inputCls}
        />
      </div>

      <div>
        <label htmlFor="notes" className={labelCls}>
          Additional notes <span className="text-muted-foreground">(optional)</span>
        </label>
        <textarea
          id="notes"
          rows={4}
          value={value.notes}
          onChange={(e) => set("notes", e.target.value)}
          className={inputCls}
        />
      </div>

      {error && (
        <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground hover:bg-card"
        >
          ← Back
        </button>
        <button
          type="submit"
          className="rounded-full bg-foreground px-6 py-2.5 text-sm font-medium text-background hover:opacity-90"
        >
          Continue to payment →
        </button>
      </div>
    </form>
  );
}